<template>
  <td
    v-bind:title="title"
    v-bind:class="tdClassObj"
    v-on:mousedown.exact="toggleGridSquare()"
    v-on:click.alt.exact="toggleAscendingGeneralHands()"
    v-on:click.shift.exact="expandGridSquareModal()"
    v-on:mouseover="handleMouseover()"
  >
    <span
      v-if="equityMatrix"
      class="display-string"
      data-toggle="tooltip"
      data-placement="top"
      v-bind:title="generalHand.equity === null ? 'Simulate to compute equity' : `${generalHand.equity}% equity`"
    >{{ generalHand.displayString }}</span>
    <span v-else class="display-string">{{ generalHand.displayString }}</span>
    <grid-square-suits
      v-if="suitsDisplayVisible"
      v-bind:generalHand="generalHand"
    />
    <weight-bar
      v-if="weightVisible"
      v-bind:weight="generalHand.averageWeight"
      v-bind:shortDeck="shortDeck"
    />
    <span
      v-if="numSpecificHandsInRangeVisbile && !equityMatrix"
      class="num-specific-hands-in-range"
    >
      {{ generalHand.numSpecificHandsInRange }}
    </span>
    <grid-square-modal
      v-if="modalVisible"
      class="grid-square-modal"
      v-bind:generalHand="generalHand"
      v-on:selectSpecificHand="$emit('selectSpecificHand', $event)"
      v-on:deselectSpecificHand="$emit('deselectSpecificHand', $event)"
      v-on:setSpecificHandAsManuallySet="$emit('setSpecificHandAsManuallySet', $event)"
      v-on:setSpecificHandAsNotManuallySet="$emit('setSpecificHandAsNotManuallySet', $event)"
      v-on:applyExactWeightToGeneralHand="$emit('applyExactWeightToGeneralHand', $event)"
      v-on:setWeightOfSpecificHand="$emit('setWeightOfSpecificHand', $event)"
      v-on:modalHidden="modalVisible = false"
    />
  </td>
</template>

<script>
import utilities from '@/services/utilities/utilities';
import GridSquareSuits from '@/components/hand-range/grid-selector/grid-square-suits';
import WeightBar from '@/components/hand-range/grid-selector/grid-square-weight-bar';
import GridSquareModal from '@/components/hand-range/grid-selector/grid-square-modal';

export default {
  name: 'grid-square',
  components: {
    'grid-square-suits': GridSquareSuits,
    'weight-bar': WeightBar,
    'grid-square-modal': GridSquareModal,
  },
  props: [
    'generalHand', 'clickAndDragContext', 'altKeyPressed', 'disabled', 'specificHandsNotAllowed', 'equityMatrix', 'requiredEquity', 'rangeConstructor', 'shortDeck'
  ],
  data: function () {
    return {
      modalVisible: false,
    };
  },
  computed: {
    tdClassObj: function () {
      let shade;

      if (this.generalHand.inRange) {
        if (this.$store.state.account.user.settings.general.visual.gridSquareColors === 'green') {
          shade = (this.generalHand.averageWeight * (this.generalHand.numSpecificHandsInRange / 12)).roundToNearestFive();
        } else {
          shade = this.generalHand.averageWeight;
        }
      } else {
        shade = 100;
      }

      if (!this.equityMatrix && !this.rangeConstructor) {
        return {
          'multicolored-grid-square': this.$store.state.account.user.settings.general.visual.gridSquareColors === 'multicolored',
          'green-grid-square': this.$store.state.account.user.settings.general.visual.gridSquareColors === 'green',

          'suited': this.generalHand.suitedOffsuitOrPair === 'suited',
          'offsuit': this.generalHand.suitedOffsuitOrPair === 'offsuit',
          'pair': this.generalHand.suitedOffsuitOrPair === 'pair',

          'in-range': this.generalHand.inRange,
          [`shade-${shade}`]: shade < 100,

          'default': !this.suitsDisplayVisible && !this.weightVisible && !this.numSpecificHandsInRangeVisbile,
          'just-suits': this.suitsDisplayVisible && !this.weightVisible && !this.numSpecificHandsInRangeVisbile,
          'just-weight-bar': !this.suitsDisplayVisible && this.weightVisible && !this.numSpecificHandsInRangeVisbile,
          'just-num-specific-hands-in-range': !this.suitsDisplayVisible && !this.weightVisible && this.numSpecificHandsInRangeVisbile,
          'suits-and-weight-bar': this.suitsDisplayVisible && this.weightVisible && !this.numSpecificHandsInRangeVisbile,
          'suits-and-num-specific-hands-in-range': this.suitsDisplayVisible && !this.weightVisible && this.numSpecificHandsInRangeVisbile,
          'weight-bar-and-num-specific-hands-in-range': !this.suitsDisplayVisible && this.weightVisible && this.numSpecificHandsInRangeVisbile,
          'suits-weight-bar-and-num-specific-hands-in-range': this.suitsDisplayVisible && this.weightVisible && this.numSpecificHandsInRangeVisbile,

          'impossible': !this.generalHand.possible,

          'nw-cursor': this.altKeyPressed && this.generalHand.spaceBetweenRanks < 3,
          'west-cursor': this.altKeyPressed && this.generalHand.spaceBetweenRanks >= 3 && this.generalHand.suitedOffsuitOrPair === 'suited',
          'north-cursor': this.altKeyPressed && this.generalHand.spaceBetweenRanks >= 3 && this.generalHand.suitedOffsuitOrPair === 'offsuit',

          'matches-five-card-hand-hover': this.generalHand.matchesFiveCardHandHover,

          'disabled': this.disabled,

          'short-deck': this.shortDeck,
        };
      }

      else if (this.equityMatrix) {
        return {
          'equity-matrix': true,
          [`diff-${ Math.floor(this.generalHand.equity - this.requiredEquity) }`]: this.generalHand.equity !== null,
        };
      }

      else if (this.rangeConstructor) {
        return {
          'red': this.generalHand.color === 'red',
          'blue': this.generalHand.color === 'blue',
          'green': this.generalHand.color === 'green',
          'yellow': this.generalHand.color === 'yellow',
          'orange': this.generalHand.color === 'orange',
          'purple': this.generalHand.color === 'purple',
          'brown': this.generalHand.color === 'brown',
          'impossible-range-constructor': this.generalHand.color === 'impossible',
          'possible-but-deselected': this.generalHand.color === null,
        }
      }
    },
    suitsDisplayVisible: function () {
      if (this.numSpecificHandsInRangeVisbile) {
        return false;
      }

      return (
        this.generalHand.numSpecificHandsInRange > 0 &&
        this.generalHand.numSpecificHandsInRange < this.generalHand.numPossibleSpecificHandsFromSuitedOffsuitOrPair
      );
    },
    weightVisible: function () {
      return this.generalHand.inRange && this.generalHand.averageWeight < 100;
    },
    numSpecificHandsInRangeVisbile: function () {
      return this.$store.state.account.user.settings.general.visual.areNumCombosInRangeVisible === true;
    },
    title: function () {
      let title = '';

      if (this.generalHand.inRange === 'partial') {
        this.generalHand.forEachPossibleSpecificHand(function (specificHand) {
          if (specificHand.inRange) {
            title += specificHand.displayString + ', ';
          }
        });
        title = title.slice(0, -2);
      }

      return title;
    },
  },
  mounted: function () {
    if (process.env.NODE_ENV !== 'testing') {
      $(this.$el).find('[data-toggle="tooltip"]').tooltip({
        container: 'body',
      });
    }
  },
  updated: function () {
    $(this.$el).find('[data-toggle="tooltip"]').tooltip('destroy');
    $(this.$el).find('[data-toggle="tooltip"]').tooltip({
      container: 'body',
    });
  },
  methods: {
    toggleGridSquare: function () {
      if (this.modalVisible) {
        return;
      }

      if (this.generalHand.inRange) {
        this.$emit('deselectGeneralHand', this.generalHand.string);
      } else {
        this.$emit('selectGeneralHand', {
          generalHandString: this.generalHand.string,
          triggeredByMouseover: false
        });
      }
    },
    toggleAscendingGeneralHands: function () {
      if (this.modalVisible) {
        return;
      }

      if (this.generalHand.inRange) {
        this.$emit('deselectAscendingGeneralHands', this.generalHand.string);
      } else {
        this.$emit('selectAscendingGeneralHands', this.generalHand.string);
      }
    },
    handleMouseover: function () {
      if (this.modalVisible) {
        return;
      }

      if (this.clickAndDragContext === 'adding' && !this.generalHand.inRange) {
        this.$emit('selectGeneralHand', {
          generalHandString: this.generalHand.string,
          triggeredByMouseover: true
        });
      } else if (this.clickAndDragContext === 'removing' && this.generalHand.inRange) {
        this.$emit('deselectGeneralHand', this.generalHand.string);
      }
    },
    expandGridSquareModal: function () {
      let self = this;

      if (self.modalVisible) {
        throw new Error('Trying to call expandGridSquareModal when modal is already visible.');
      }

      if (self.specificHandsNotAllowed) {
        return;
      }

      self.modalVisible = true;
      self.$nextTick(function () {
        $(self.$el).find('.grid-square-modal').modal();
      })
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "src/assets/arrow-cursors";
  @import "src/assets/grid-square-shades";
  @import "src/assets/mixins";
  @import "node_modules/bootstrap-sass/assets/stylesheets/bootstrap/variables";

  td {
    @include noselect;
    text-align: center;
    vertical-align: middle;
    cursor: cell;
    width: 32.1px;
    height: 32.1px;
    font-size: 12px;
    border-radius: 5px;
    position: relative;

    &.just-suits {
      .display-string {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        top: 4px;
      }

      .grid-square-suits {
        position: absolute;
        font-size: 13px;
        top: 15px;
        width: fit-content;
        left: 50%;
        transform: translate(-50%, 0);
      }
    }

    &.suits-and-weight-bar {
      .display-string {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        top: 3px;
        font-size: 11px;
      }

      .grid-square-suits {
        position: absolute;
        font-size: 10px;
        top: 13px;
        width: fit-content;
        left: 50%;
        transform: translate(-50%, 0);

        &.more-than-one {
          font-size: 6px;
          top: 17px;
        }
      }

      .grid-square-weight-bar {
        bottom: 3px;
      }
    }

    &.just-num-specific-hands-in-range, &.weight-bar-and-num-specific-hands-in-range {
      .display-string {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        top: 0;
      }

      .num-specific-hands-in-range {
        color: lighten($gray-base, 42%);
        position: absolute;
        left: 25%;
        transform: translate(-25%, 0);
        font-size: 9px;
        top: 12px;
        width: fit-content;
      }

      .grid-square-weight-bar {
        display: none;
      }
    }

    &.suits-and-num-specific-hands-in-range, &.suits-weight-bar-and-num-specific-hands-in-range {
      .display-string {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        top: 0;
      }

      .grid-square-suits {
        position: absolute;
        font-size: 13px;
        top: 9px;
        width: fit-content;
        left: 75%;
        transform: translate(-75%, 0);
      }

      .num-specific-hands-in-range {
        color: lighten($gray-base, 42%);
        position: absolute;
        left: 25%;
        transform: translate(-25%, 0);
        font-size: 9px;
        top: 12px;
        width: fit-content;
      }

      .grid-square-weight-bar {
        display: none;
      }
    }

    &.impossible {
      color: lightgrey;
      background-color: white !important;
      pointer-events: none;

      .num-specific-hands-in-range {
        color: lightgrey;
      }
    }

    &.alt-key-pressed {
      cursor: pointer;
    }

    &.matches-five-card-hand-hover {
      background-color: #9c27b0d4 !important;
    }

    &.disabled {
      pointer-events: none;
    }

    // equity matrix
    &.equity-matrix {
      cursor: auto;
    }

    // range constructor
    &.red { background-color: #e27c79; }
    &.blue { background-color: #337ab7d4; }
    &.green { background-color: #80c780; }
    &.yellow { background-color: #e2cd0ed9; }
    &.orange { background-color: #ff9800e0; }
    &.purple { background-color: #9c27b0c7; }
    &.brown { background-color: #ab6146d4; }
    &.impossible-range-constructor {
      color: lightgrey;
      background-color: white;
    }
    &.possible-but-deselected {
      color: auto;
      background-color: lighten($gray-base, 95%);;
    }

    // short-deck
    &.short-deck {
      width: 36.1px;
      height: 36.1px;
      font-size: 12px;

      .grid-square-weight-bar {
        bottom: 5px;
      }

      &.just-suits {
        .display-string {
          position: absolute;
          left: 50%;
          transform: translate(-50%, 0);
          top: 5px;
        }

        .grid-square-suits {
          position: absolute;
          font-size: 13px;
          top: 17px;
          width: fit-content;
          left: 50%;
          transform: translate(-50%, 0);

          &.more-than-one {
            top: 18px;

            & /deep/ .more-than-one-text {
              font-size: 10px;
            }
          }
        }
      }

      &.suits-and-weight-bar {
        .display-string {
          position: absolute;
          left: 50%;
          transform: translate(-50%, 0);
          top: 3px;
          font-size: 12px;
        }

        .grid-square-suits {
          position: absolute;
          font-size: 12px;
          top: 15px;
          width: fit-content;
          left: 50%;
          transform: translate(-50%, 0);

          &.more-than-one {
            top: 15px;

            & /deep/ .more-than-one-text {
              font-size: 10px;
            }
          }
        }

        .grid-square-weight-bar {
          bottom: 4px;
          height: 2px;
        }
      }
    }
  }

  @media (max-width: 475px) {
    td {
      width: 32px;
      height: 32px;
      font-size: 10px;
    }
  }

  @media (max-width: 460px) {
    td {
      width: 30px;
      height: 30px;
      font-size: 10px;
    }
  }

  @media (max-width: 450px) {
    td {
      width: 28px;
      height: 28px;
      font-size: 10px;
    }
  }

  @media (max-width: 375px) {
    td {
      font-size: 10.5px;
      padding: 1px;
      border-radius: 3px;
    }
  }

  @media (max-width: 360px) {
    td {
      font-size: 10px;
    }
  }

  @media (max-width: 320px) {
    td {
      font-size: 8.3px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/hand-range/grid-selector/grid-square.vue